import React, { useEffect, useMemo, useState } from 'react';
import { Container, Row, Col, Alert, Spinner } from 'react-bootstrap';
import Swiper from 'react-id-swiper';
import { Styles } from "./styles/testimonialSlider.js";
import { TESTIMONIALS_ENDPOINT, API_BASE_URL } from '../config/api';

const SECTION_TITLE = 'Hear From Our Students';
const DEFAULT_BACKGROUND = `${process.env.PUBLIC_URL}/assets/images/vd-bg.jpg`;
const PLACEHOLDER_AVATAR = 'https://via.placeholder.com/120x120?text=User';

const TestimonialSlider = () => {
    const [testimonials, setTestimonials] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        const controller = new AbortController();

        const loadTestimonials = async () => {
            setLoading(true);
            setError('');
            try {
                const response = await fetch(TESTIMONIALS_ENDPOINT, { signal: controller.signal });
                if (!response.ok) {
                    const payload = await response.json().catch(() => ({}));
                    throw new Error(payload.message || 'Unable to load testimonials.');
                }
                const payload = await response.json();
                const list = Array.isArray(payload.testimonials) ? payload.testimonials : [];
                const published = list
                    .filter((item) => item && item.status === 'published')
                    .sort((a, b) => {
                        const left = a?.updatedAt ? new Date(a.updatedAt).getTime() : 0;
                        const right = b?.updatedAt ? new Date(b.updatedAt).getTime() : 0;
                        return right - left;
                    });
                setTestimonials(published);
            } catch (err) {
                if (err.name !== 'AbortError') {
                    setError(err.message || 'Unable to load testimonials.');
                    setTestimonials([]);
                }
            } finally {
                if (!controller.signal.aborted) {
                    setLoading(false);
                }
            }
        };

        loadTestimonials();
        return () => controller.abort();
    }, []);

    const sliderSettings = useMemo(() => ({
        slidesPerView: 2,
        loop: true,
        speed: 1000,
        autoplay: {
            delay: 3000,
            disableOnInteraction: false
        },
        spaceBetween: 30,
        watchSlidesVisibility: true,
        pagination: {
            el: '.slider-dot.text-center',
            clickable: true
        },
        breakpoints: {
            0: {
                slidesPerView: 1
            },
            576: {
                slidesPerView: 1
            },
            768: {
                slidesPerView: 2
            },
            992: {
                slidesPerView: 2
            }
        }
    }), []);

    const resolvedTestimonials = useMemo(
        () => testimonials.map((testimonial) => {
            const avatarUrl = (testimonial.avatarUrl || '').trim();
            const resolvedAvatar = avatarUrl
                ? (avatarUrl.startsWith('http') ? avatarUrl : `${API_BASE_URL}${avatarUrl}`)
                : PLACEHOLDER_AVATAR;
            const quote = (testimonial.quote || '').trim();
            return {
                id: testimonial._id || testimonial.id || resolvedAvatar,
                authorName: testimonial.authorName || 'Anonymous',
                authorTitle: testimonial.authorTitle || '',
                quote: quote.length > 320 ? `${quote.slice(0, 317)}…` : quote,
                avatar: resolvedAvatar
            };
        }),
        [testimonials]
    );

    return (
        <Styles>
            <section className="testimonial-area" style={{ backgroundImage: `url(${DEFAULT_BACKGROUND})` }}>
                <Container>
                    <Row>
                        <Col md="12">
                            <div className="sec-title text-center">
                                <h4>{SECTION_TITLE}</h4>
                            </div>
                        </Col>
                        <Col md="12" className="testimonial-slider">
                            {loading && (
                                <div className="text-center py-5">
                                    <Spinner animation="border" role="status" />
                                </div>
                            )}
                            {!loading && error && (
                                <Alert variant="danger" className="text-center mb-0">
                                    {error}
                                </Alert>
                            )}
                            {!loading && !error && resolvedTestimonials.length === 0 && (
                                <Alert variant="light" className="text-center mb-0">
                                    Testimonials coming soon.
                                </Alert>
                            )}
                            {!loading && !error && resolvedTestimonials.length > 0 && (
                                <Swiper key={resolvedTestimonials.length} {...sliderSettings}>
                                    {resolvedTestimonials.map((item) => (
                                        <div className="slider-item" key={item.id}>
                                            <div className="desc">
                                                <blockquote>
                                                    <p>{item.quote}</p>
                                                </blockquote>
                                            </div>
                                            <div className="writer">
                                                <img src={item.avatar} className="slider-image" alt={item.authorName} />
                                                <h6>{item.authorName}</h6>
                                                {item.authorTitle && <p>{item.authorTitle}</p>}
                                            </div>
                                        </div>
                                    ))}
                                </Swiper>
                            )}
                        </Col>
                    </Row>
                </Container>
            </section>
        </Styles>
    );
};

export default TestimonialSlider;
